import React, {useEffect} from 'react'
import styled from '@emotion/styled'
import {AnimatePresence, motion} from 'motion/react'

import {Card} from '@vio/atlas/Card'
import {Box} from '@vio/atlas/helpers/Box'
import {Button} from '@vio/atlas/src/components/designSystem/Button'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {cssTheme} from '@vio/atlas/src/themes'
import {Text} from '@vio/atlas/Text'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {Offer} from '@vio/core/src/offer/types/offer'

import {RoomOfferCardPriceInfo} from '../RoomOfferCardPriceInfo'
import {useTrackRoomOfferViewed} from '../Rooms/RoomOffer/useTrackRoomOfferViewed'

const MOBILE_FOOTER_HEIGHT = 69
const DESKTOP_FOOTER_HEIGHT = 92

// Height animation instead of translate - a translate would leave an empty
// strip behind because the footer is an in-flow flex child.
const AnimatedLayer = styled(motion.div)`
  flex-shrink: 0;
  width: 100%;
  z-index: 1;
  overflow: hidden;
`

const FooterCard = styled(Card)<{isMobile: boolean; height: number}>`
  border-top: 1px solid ${cssTheme.colors.border.neutral.secondary};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s500};
  height: ${({height}) => height}px;
  box-sizing: border-box;
`

interface FooterProps {
  isFooterVisible: boolean
  children: React.ReactNode
  footerHeight: number
}

interface Props {
  isFooterVisible: boolean
  offer: Offer
  hotelId: string
  viewDealsClickHandler: () => void
}

const Content = ({
  hotelId,
  offer,
  onClick
}: {
  hotelId: string
  offer: Offer
  onClick: () => void
}) => {
  return (
    <Box
      display="flex"
      flexDirection="row"
      gap="s200"
      justifyContent="space-between"
      width="100%"
    >
      <Box display="flex" flexDirection="column">
        <Text variant="bodyXS">
          <FormattedMessageWrapper id="from" defaultMessage="From" />
        </Text>

        <RoomOfferCardPriceInfo hotelId={hotelId} offer={offer} isCompact />
      </Box>

      <Button style={{flexShrink: 0, whiteSpace: 'nowrap'}} onClick={onClick}>
        <FormattedMessageWrapper id="viewDeals" defaultMessage="View deals" />
      </Button>
    </Box>
  )
}

const Footer = ({children, isFooterVisible, footerHeight}: FooterProps) => {
  return (
    <AnimatePresence>
      {isFooterVisible && (
        <AnimatedLayer
          key="ap-bottom-cta"
          initial={{height: 0}}
          animate={{height: footerHeight}}
          exit={{height: 0}}
          transition={{duration: 0.5}}
        >
          {children}
        </AnimatedLayer>
      )}
    </AnimatePresence>
  )
}

export const OverlayCompactFooter = ({
  isFooterVisible,
  offer,
  hotelId,
  viewDealsClickHandler
}: Props) => {
  const {inView, ref} = useInView({
    triggerOnce: true,
    threshold: 0.5,
    initialInView: true
  })

  const {trackOfferViewed} = useTrackRoomOfferViewed({
    offer
  })

  useEffect(() => {
    if (inView) {
      trackOfferViewed()
    }
  }, [inView, trackOfferViewed])

  const {isMobile, isDesktopXs} = useDeviceLayout()
  const isMobileOrTablet = isMobile || isDesktopXs
  const footerHeight = isMobileOrTablet
    ? MOBILE_FOOTER_HEIGHT
    : DESKTOP_FOOTER_HEIGHT
  return (
    <Footer isFooterVisible={isFooterVisible} footerHeight={footerHeight}>
      <FooterCard
        isMobile={isMobileOrTablet}
        height={footerHeight}
        backgroundColorPath="background.neutral.primary"
        hasRoundedBottom={false}
        ref={ref}
      >
        <Content
          hotelId={hotelId}
          offer={offer}
          onClick={viewDealsClickHandler}
        />
      </FooterCard>
    </Footer>
  )
}
